<script setup>
import { computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import wootConstants from 'dashboard/constants/globals';
import StaffChatList from 'dashboard/components/staffChat/StaffChatList.vue';
import StaffChatBox from 'dashboard/components/staffChat/StaffChatBox.vue';

const props = defineProps({
  staffChatId: {
    type: Number,
    default: 0,
  },
});

const route = useRoute();
const router = useRouter();
const store = useStore();
const { uiSettings } = useUISettings();

const selectedChat = useMapGetter('staffChats/getSelectedStaffChat');

const isOnExpandedLayout = computed(() => {
  const { LAYOUT_TYPES } = wootConstants;
  const { conversation_display_type: displayType = LAYOUT_TYPES.CONDENSED } =
    uiSettings.value;
  return displayType !== LAYOUT_TYPES.CONDENSED;
});

const showChatList = computed(() => {
  return isOnExpandedLayout.value ? !props.staffChatId : true;
});

const showMessageView = computed(() => {
  return props.staffChatId ? true : !isOnExpandedLayout.value;
});

const findChat = () => {
  const chats = store.getters['staffChats/getStaffChats'];
  return chats.find(chat => chat.id === props.staffChatId);
};

const setActiveChat = async () => {
  if (!props.staffChatId) {
    store.dispatch('staffChats/clearActiveStaffChat');
    return;
  }

  let chat = findChat();
  if (!chat?.id) {
    await store.dispatch('staffChats/fetchStaffChat', props.staffChatId);
    chat = findChat();
  }

  if (chat?.id) {
    await store.dispatch('staffChats/setActiveStaffChat', chat);
  }
};

watch(
  () => props.staffChatId,
  () => setActiveChat()
);

onMounted(async () => {
  await store.dispatch('staffChats/fetchStaffChats');
  await store.dispatch('agents/get');
  setActiveChat();
});

const onChatSelected = chat => {
  router.push({
    name: 'staff_chat_conversation',
    params: {
      accountId: route.params.accountId,
      staffChatId: chat.id,
    },
  });
};

const onChatDeleted = deletedChatId => {
  if (props.staffChatId === deletedChatId) {
    router.push({
      name: 'staff_chat_list',
      params: {
        accountId: route.params.accountId,
      },
    });
  }
};
</script>

<template>
  <section class="flex w-full h-full min-w-0">
    <StaffChatList
      v-if="showChatList"
      :active-chat-id="staffChatId"
      @select-chat="onChatSelected"
      @chat-deleted="onChatDeleted"
    />
    <StaffChatBox
      v-if="showMessageView"
      :chat="selectedChat"
      :is-on-expanded-layout="isOnExpandedLayout"
      @chat-deleted="onChatDeleted(staffChatId)"
    />
  </section>
</template>
